import type { KeyActivity, Milestone, Objective } from '../types/milestones';

export function formatSubawardeeLabel(
  name: string,
  labelById: Record<string, string> = {}
): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  return labelById[trimmed] ?? trimmed;
}

/** Subawardee names attached to a key activity in component-project milestone trees. */
export function getKeyActivitySubawardeeLabels(
  keyActivity: KeyActivity,
  labelById: Record<string, string> = {}
): string[] {
  const labels: string[] = [];

  if (keyActivity.subawardee?.trim()) {
    labels.push(formatSubawardeeLabel(keyActivity.subawardee, labelById));
  }

  for (const name of keyActivity.subawardees ?? []) {
    const label = formatSubawardeeLabel(name, labelById);
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  }

  return labels;
}

/** Unique subawardee labels across all key activities on an objective. */
export function getObjectiveSubawardeeLabels(
  objective: Objective,
  labelById: Record<string, string> = {}
): string[] {
  const labels: string[] = [];
  for (const keyActivity of objective.key_activities) {
    for (const label of getKeyActivitySubawardeeLabels(keyActivity, labelById)) {
      if (label && !labels.includes(label)) {
        labels.push(label);
      }
    }
  }
  return labels;
}

export function getMilestoneSubawardeeLabel(
  milestone: Milestone,
  labelById: Record<string, string> = {}
): string | null {
  const raw = milestone.subawardee?.trim();
  if (!raw) return null;
  return formatSubawardeeLabel(raw, labelById);
}

/** Per-milestone label when unambiguous; falls back to sole key-activity subawardee. */
export function getMilestoneSubawardeeDisplay(
  milestone: Milestone,
  keyActivityLabels: string[],
  labelById: Record<string, string> = {}
): string | null {
  const milestoneLabel = getMilestoneSubawardeeLabel(milestone, labelById);
  if (milestoneLabel) return milestoneLabel;
  if (keyActivityLabels.length === 1) return keyActivityLabels[0];
  return null;
}
